import { EVENT_KEYS, FILTER_FORM_DEFAULT_VALUES } from '../../util/constants.js'
import {
  callGetPublicConnections,
  callGetPublicOrganisationConnections,
  callGetConnections,
  callGetOrganisationConnections,
  callGetOrganisations,
  callGetConnection,
  callGetHistory,
  callUpsertConnection,
  callUpsertPublicConnection,
  callValidateConnection,
  callSaveConnection,
  callUnsaveConnection,
  callRemoveConnection,
  callEnableOrganisationConnection,
  callDisableOrganisationConnection,
  callSendMessage,
  callLogContactPhoned,
  callLogContactLinkedIn,
  callLogActivity,
  callGetReports,
  callGetReport,
  callGetReportsYears,
  callGetReportsStats,
  callUpsertReport,
  callRemoveReport,
  callGetPublicConnection,
} from '../../api/connections.js'
import {
  triggerAlert,
  triggerErrorGeneric,
  mapConnection,
  mapReport,
} from '../util.js'
import { EventBus } from '../../../js/plugins/event-bus.js'

/**
 * Gets the public connections for this tenant, according to filters.
 *
 * @param {object} context
 * @param {object} payload includes pagination (page), filters
 * @returns {object|boolean} data or api failure
 */
export async function dispatchUpdatePublicConnections(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetPublicConnections(tenantId, payload)

    if (response?.data?.success) {
      const data = response.data.data?.data.map(mapConnection)
      context.commit('UPDATE_CONNECTIONS', data)
      triggerAlert(response)
      return response.data.data
    }

    context.commit('CLEAR_CONNECTIONS')
    triggerAlert(response)
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets the organisation/contributor/national connections for this tenant, according to filters.
 *
 * @param {object} context
 * @param {object} payload includes pagination (page), filters
 * @returns {object|boolean} data or api failure
 */
export async function dispatchUpdatePublicOrganisationConnections(
  context,
  payload,
) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetPublicOrganisationConnections(
      tenantId,
      payload,
    )

    if (response?.data?.success) {
      // Depending on whether paginate or randomise is used in the request, 
      // we sometimes receive a pagination object and sometimes just an array of contacts
      // check for both
      const contacts = response.data.data.contacts;
      const dataArray = contacts.data ? contacts.data : contacts;
      const data = dataArray.map(mapConnection)
      context.commit('UPDATE_ORGANISATION_CONNECTIONS', data)
      triggerAlert(response)
      return response.data.data
    }

    context.commit('CLEAR_ORGANISATION_CONNECTIONS')
    triggerAlert(response)
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets the connections for this tenant, according to filters.
 *
 * @param {object} context
 * @param {object} payload includes page/view context (SAVED, LOCAL), pagination (page), filters
 * @returns {object|boolean} data or api failure
 */
export async function dispatchUpdateConnections(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetConnections(tenantId, payload)

    if (response?.data?.success) {
      const data = response.data.data?.data.map(mapConnection)
      context.commit('UPDATE_CONNECTIONS', data)
      triggerAlert(response)
      return response.data.data
    }

    context.commit('CLEAR_CONNECTIONS')
    triggerAlert(response)
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets the organisation/contributor/national connections for this tenant, according to filters.
 *
 * @param {object} context
 * @param {object} payload includes pagination (page), filters
 * @returns {object|boolean} data or api failure
 */
export async function dispatchUpdateOrganisationConnections(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetOrganisationConnections(tenantId, payload)

    if (response?.data?.success) {
      const data = response.data.data?.data.map(mapConnection)
      context.commit('UPDATE_ORGANISATION_CONNECTIONS', data)
      triggerAlert(response)
      return response.data.data
    }

    context.commit('CLEAR_ORGANISATION_CONNECTIONS')
    triggerAlert(response)
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets the organisations saved to this tenant.
 *
 * @param {object} context
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateOrganisations(context) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetOrganisations(tenantId)

    if (response?.data?.success) {
      const data = response.data.data
      context.commit('UPDATE_ORGANISATIONS', data)
      triggerAlert(response)
      return true
    }

    context.commit('CLEAR_ORGANISATIONS')
    triggerAlert(response)
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/*************************** CONNECTION-SPECIFIC ***************************/

/**
 * Gets full details/info for this connection.
 *
 * @param {object} context
 * @param {object} payload includes 'connectionId', context key ('MANAGER', 'PUBLIC)
 * @returns {*} connection details, if successful; null, if unsuccessful
 */
export async function dispatchUpdateConnection(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const fnMap = {
      MANAGER: callGetConnection,
      PUBLIC: callGetPublicConnection,
    }
    const getFn = fnMap[payload.context || 'PUBLIC']
    const response = await getFn(tenantId, payload.connectionId, payload?.data)

    if (response?.data?.success) {
      const data = mapConnection(response.data?.data)
      triggerAlert(response)
      return data
    }

    triggerAlert(response)
    return null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets the message and report history for a connection.
 *
 * @param {object} context
 * @param {number} connectionId
 * @returns {*} list of history, if successful; false, if unsuccessful
 */
export async function dispatchUpdateHistory(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload.id
    const response = await callGetHistory(tenantId, connectionId, payload)

    if (response?.data?.success) {
      const data = response.data.data?.data
      const tagsGetter = context.rootGetters['shared/getTagsMappedByCategory']
      const reportData = data.map((report) => mapReport(report, tagsGetter))

      context.commit('UPDATE_REPORTS', reportData)
      triggerAlert(response)
      return response.data.data
    }

    triggerAlert(response)
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Creates or updates a connection.
 *
 * @param {object} context
 * @param {object} payload includes connection details, context key ('MANAGER', 'PUBLIC)
 * @returns {boolean} api success/failure
 */
export async function dispatchUpsertConnection(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const fnMap = {
      MANAGER: callUpsertConnection,
      PUBLIC: callUpsertPublicConnection,
    }
    const upsertFn = fnMap[payload.context || 'PUBLIC']
    const response = await upsertFn(
      tenantId,
      payload.id || null,
      payload.formData,
    )

    // @TODO we should refresh connection data, but which context ?
    // Perhaps let router handle it
    // await context.dispatch('dispatchUpdateConnections', {
    //   context: payload.context,
    // })
    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Validates a sign up from a connection from email / token.
 *
 * @param {object} context
 * @param {number} connectionId
 * @returns {*} true if success, false if fail
 */
export async function dispatchValidateConnection(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callValidateConnection(tenantId, payload)

    if (response?.data?.success) {
      return true
    }

    triggerAlert(response)
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Saves a (local) connection to this tenant.
 * Response includes pagination, emitted back to the parent page.
 *
 * @param {object} context
 * @param {object} payload includes connectionId and page/view context (SAVED, LOCAL, CW)
 * @returns {boolean} api success/failure
 */
export async function dispatchSaveConnection(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload?.connectionId || null
    const response = await callSaveConnection(tenantId, connectionId)

    const filters = context.getters.getFilterFormPayload
    const connectionsResponse = await context.dispatch(
      'dispatchUpdateConnections',
      {
        context: payload.context,
        filters,
      },
    )

    EventBus.$emit(
      EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION,
      connectionsResponse,
    )

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Unsaves a (local) connection from this tenant. Does not remove the connection
 * from the CareerWise system (they still appear in the general list of connections).
 * Response includes pagination, emitted back to the parent page.
 *
 * @param {object} context
 * @param {object} payload includes connectionId and page/view context (SAVED, LOCAL, CW)
 * @returns {boolean} api success/failure
 */
export async function dispatchUnsaveConnection(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload?.connectionId || null
    const response = await callUnsaveConnection(tenantId, connectionId)

    const filters = context.getters.getFilterFormPayload
    const connectionsResponse = await context.dispatch(
      'dispatchUpdateConnections',
      {
        context: payload.context,
        filters,
      },
    )

    EventBus.$emit(
      EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION,
      connectionsResponse,
    )

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Removes (deletes) a (local) connection from CareerWise. This detaches the
 * connection from this tenant (as opposed to just unsaving them).
 * Response includes pagination, emitted back to the parent page.
 *
 * @param {object} context
 * @param {object} payload includes connectionId and page/view context (SAVED, LOCAL, CW)
 * @returns {boolean} api success/failure
 */
export async function dispatchRemoveConnection(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload?.connectionId || null
    const response = await callRemoveConnection(tenantId, connectionId)

    const filters = context.getters.getFilterFormPayload
    const connectionsResponse = await context.dispatch(
      'dispatchUpdateConnections',
      {
        context: payload.context,
        filters,
      },
    )

    EventBus.$emit(
      EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION,
      connectionsResponse,
    )

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Enables an organisation (national) connection for this tenant.
 * Response includes pagination, emitted back to the parent page.
 *
 * @param {object} context
 * @param {object} payload includes connectionId
 * @returns {boolean} api success/failure
 */
export async function dispatchEnableOrganisationConnection(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload?.connectionId || null
    const response = await callEnableOrganisationConnection(
      tenantId,
      connectionId,
    )

    const filters = context.getters.getFilterFormPayload
    const connectionsResponse = await context.dispatch(
      'dispatchUpdateOrganisationConnections',
      {
        filters,
      },
    )

    EventBus.$emit(
      EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION,
      connectionsResponse,
    )

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Disables (blocks) an organisation (national) connection for this tenant.
 * Response includes pagination, emitted back to the parent page.
 *
 * @param {object} context
 * @param {object} payload includes connectionId
 * @returns {boolean} api success/failure
 */
export async function dispatchDisableOrganisationConnection(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload?.connectionId || null
    const response = await callDisableOrganisationConnection(
      tenantId,
      connectionId,
    )

    const filters = context.getters.getFilterFormPayload
    const connectionsResponse = await context.dispatch(
      'dispatchUpdateOrganisationConnections',
      {
        filters,
      },
    )

    EventBus.$emit(
      EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION,
      connectionsResponse,
    )

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Sends an email to a connection. Backend logs the activity.
 *
 * @param {object} context
 * @param {object} payload includes connectionId, message details, userId to log
 * @returns {boolean} api success/failure
 */
export async function dispatchSendMessage(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload?.connectionId || null
    const response = await callSendMessage(tenantId, connectionId, payload)

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Logs a click of the phone number of a contact.
 *
 * @param {object} context
 * @param {object} payload includes connectionId, tenantId
 * @returns {boolean} api success/failure
 */
export async function dispatchLogContactPhoned(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload?.connectionId
    const response = await callLogContactPhoned(tenantId, connectionId)

    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Logs a click of the phone number of a contact.
 *
 * @param {object} context
 * @param {object} payload includes connectionId, tenantId
 * @returns {boolean} api success/failure
 */
export async function dispatchLogContactLinkedIn(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const connectionId = payload?.connectionId
    const response = await callLogContactLinkedIn(tenantId, connectionId)

    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/*************************** REPORTS ***************************/

/**
 * Gets the reports for this tenant, according to filters.
 *
 * @param {object} context
 * @param {object} payload includes pagination, filters
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateReports(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetReports(tenantId, payload)

    if (response?.data?.success) {
      const data = response.data?.data
      // Reports
      const tagsGetter = context.rootGetters['shared/getTagsMappedByCategory']
      const reportsData = data.reports.map((report) =>
        mapReport(report, tagsGetter),
      )
      // Reports stats
      const reportsStats = data.stats

      context.commit('UPDATE_REPORTS', reportsData)
      context.commit('UPDATE_REPORTS_STATS', reportsStats)
      triggerAlert(response)
      return data.pagination
    }

    triggerAlert(response)
    context.commit('CLEAR_REPORTS')
    context.commit('CLEAR_REPORTS_STATS')
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Clears the store of the current reports
 * @param {object} context
 */
export async function dispatchClearReports(context) {
  context.commit('CLEAR_REPORTS')
}

/**
 * Gets full details/info for this report.
 *
 * @param {object} context
 * @param {number} reportId
 * @returns {*} report details, if successful; null, if unsuccessful
 */
export async function dispatchUpdateReport(context, reportId) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetReport(tenantId, reportId)

    if (response?.data?.success) {
      const data = response.data.data
      const tagsGetter = context.rootGetters['shared/getTagsMappedByCategory']
      const reportData = mapReport(data, tagsGetter)

      triggerAlert(response)
      return reportData
    }

    triggerAlert(response)
    return null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets all the years for which reports exist.
 *
 * @param {object} context
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateReportsYears(context) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetReportsYears(tenantId)

    if (response?.data?.success) {
      const data = response.data.data
      context.commit('UPDATE_REPORTS_YEARS', data)
      triggerAlert(response)
      return response.data.data
    }

    context.commit('CLEAR_REPORTS_YEARS')
    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets the reports overview stats.
 *
 * @param {object} context
 * @param {object} payload includes filters
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateReportsStats(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const response = await callGetReportsStats(tenantId, payload)

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Creates or updates a report.
 *
 * @param {object} context
 * @param {object} payload includes report details
 * @returns {boolean} api success/failure
 */
export async function dispatchUpsertReport(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const reportId = payload?.id || null
    const response = await callUpsertReport(tenantId, reportId, payload)
    await context.dispatch('dispatchInitFilters', 'reports')

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Updates the selected connections for the current report.
 *
 * @param {object} context
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateSelectedReportConnections(
  context,
  payload,
) {
  if (payload) {
    context.commit('UPDATE_REPORT_SELECTED_CONNECTIONS', payload)
  } else {
    context.commit('CLEAR_REPORT_SELECTED_CONNECTIONS')
  }
}

/**
 * Removes (deletes) a report from CareerWise. This detaches the
 * report from this tenant (as opposed to just unsaving them).
 *
 * @param {object} context
 * @param {object} payload includes reportId and page/view context (SAVED, LOCAL, CW)
 * @returns {boolean} api success/failure
 */
export async function dispatchRemoveReport(context, payload) {
  try {
    const tenantId = context.rootGetters['shared/getTenantId']
    const reportId = payload?.reportId || null
    const response = await callRemoveReport(tenantId, reportId)

    EventBus.$emit(EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION_REPORTS)

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/*************************** FILTERS ***************************/

/**
 * Clears filters AND resets store version because
 * watchers keeping values in sync may have been destroyed.
 *
 * @param {object} context
 */
export async function dispatchInitFilters(context, routeContext) {
  try {
    await context.dispatch('dispatchClearFilters')
    await context.dispatch('dispatchUpdateFilterForm')

    if (routeContext === 'reports') {
      await context.dispatch('dispatchUpdateReportsYears')
      const filterForm = context.getters.getFilterForm
      const reportsYears = context.getters.getReportsYearsOptions
      await context.dispatch('dispatchUpdateFilterForm', {
        ...filterForm,
        dateYear: reportsYears?.[0] || null,
      })
    }
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Updates store filter form to keep filter form data in sync globally.
 * See getSelectedFilters for payload/data structure.
 *
 * @param {object} context
 * @param {object} payload filter form object
 */
export async function dispatchUpdateFilterForm(context, payload = null) {
  try {
    if (payload) {
      context.commit('UPDATE_FILTER_FORM', payload)
    } else {
      context.commit('CLEAR_FILTER_FORM')
    }
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Removes a selected filter from the actual filter form instance
 * (which updates the store filter form via watchers).
 *
 * @param {object} context
 * @param {object} payload filter form object
 * @todo make more data-driven
 */
export async function dispatchRemoveFilter(context, opt) {
  try {
    const filterForm = context.getters.getFilterForm
    const value = JSON.parse(JSON.stringify(filterForm[opt.name]))
    if (value) {
      if (Array.isArray(value)) {
        // Form tags (custom input): we need to splice the id from the appropriate array
        const index = value.findIndex((x) => parseInt(x) === opt.data.value)
        value.splice(index, 1)
        EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
          type: opt.name,
          selectedOptions: value,
        })
      } else if (opt.name === 'dateYear') {
        // @TODO treeshake
        // Do nothing (date year filter cannot be removed)
      } else if (opt.name.includes('date')) {
        // Date input
        EventBus.$emit(
          EVENT_KEYS.CO_VUE_FORMULATE_FILTER_FORM_DATE_UPDATED,
          opt.name,
        )
      } else if (opt.name === 'keywords') {
        // @TODO treeshake
        // Keywords/search input
        EventBus.$emit(
          EVENT_KEYS.CO_VUE_FORMULATE_FILTER_FORM_KEYWORDS_UPDATED,
          null,
        )
      } else {
        // Form toggle (custom input)
        EventBus.$emit(
          EVENT_KEYS.CO_VUE_FORMULATE_TOGGLE_UPDATED,
          FILTER_FORM_DEFAULT_VALUES[opt.name],
        )
      }
    }
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Clears/resets the actual filter form instance values entirely
 * (which updates the store filter form via watchers).
 *
 * @param {object} context
 * @todo make more data-driven
 */
export async function dispatchClearFilters(context) {
  try {
    const filterForm = context.getters.getFilterForm
    for (const key of Object.keys(filterForm)) {
      const value = JSON.parse(JSON.stringify(filterForm[key]))
      if (value) {
        if (Array.isArray(value)) {
          // Form tags (custom input): remove all the array elements
          EventBus.$emit(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, {
            type: key,
            selectedOptions: [],
          })
        } else if (key === 'dateYear') {
          EventBus.$emit(
            EVENT_KEYS.CO_VUE_FORMULATE_FILTER_FORM_DATE_YEAR_UPDATED,
            null,
          )
        } else if (key.includes('date')) {
          // Date input
          EventBus.$emit(
            EVENT_KEYS.CO_VUE_FORMULATE_FILTER_FORM_DATE_UPDATED,
            key,
          )
        } else if (key === 'keywords') {
          // @TODO treeshake
          // Keywords/search input
          EventBus.$emit(
            EVENT_KEYS.CO_VUE_FORMULATE_FILTER_FORM_KEYWORDS_UPDATED,
            null,
          )
        } else {
          // Form toggle (custom input)
          EventBus.$emit(
            EVENT_KEYS.CO_VUE_FORMULATE_TOGGLE_UPDATED,
            FILTER_FORM_DEFAULT_VALUES[key],
          )
        }
      }
    }
  } catch {
    triggerErrorGeneric()
  }
}

/*************************** POSTS (could move this?) ***************************/

/**
 * Updates the selected connections for the current post.
 *
 * @param {object} context
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateSelectedPostConnections(context, payload) {
  if (payload) {
    context.commit('UPDATE_POST_SELECTED_CONNECTIONS', payload)
  } else {
    context.commit('CLEAR_POST_SELECTED_CONNECTIONS')
  }
}
